import React, { useState, useEffect, useCallback } from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { MessageCard } from "../components/MessageCard";
import axios, { CancelToken } from "axios";
import { getCancelTokenSource } from "../utils/requests";
import { tryGetProp } from "../utils";

interface FetcherProps {
	// async action to fetch data
	action: (cancelToken?: CancelToken) => Promise<any>;
	//optional property to make spinner invisible.
	hidden?: boolean;
	// render function to call with fetched data
	children: (payload: any) => JSX.Element;
	//optional function that should be used to test whether or not the fether should fetch or use the data in state.
	loadedData?: () => any;
	//optional function that should return an element meant to be displayed instead of the loading indicator.
	placeHolder?: () => React.ReactElement;
	//method for setting the loaded data in state.
	setStateData?: (payload: any) => any;
}

const MSG_BY_STATUS_CODE = {
	403: "You are not authorized to view this page.",
	500: "An unexpected error occurred.",
};

const useFetcher = (
	action: (cancelToken?: CancelToken) => Promise<any>,
	shouldFetch: boolean,
): [any, boolean, string | null, number] => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [errorStatusCode, setErrorStatusCode] = useState(0);
	const [data, setData] = useState(null);

	const loadData = useCallback(
		async function (cancelToken?: CancelToken) {
			let cancelled = false;
			try {
				setLoading(true);
				const actionData = await action(cancelToken);
				setData(actionData);
			} catch (e: any) {
				cancelled = axios.isCancel(e);
				if (!cancelled) {
					console.error(e);
					const statusCode = tryGetProp(0, () => e.response.status);

					if (statusCode === 401) {
						throw e;
					}
					const msg = e.message || e;
					if (e.response) {
						setErrorStatusCode(statusCode);
						setError(e.response.data || msg);
					} else {
						setError(msg);
					}
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		},
		[action],
	);

	useEffect(() => {
		const cancelTokenSource = getCancelTokenSource();
		if (shouldFetch) {
			loadData(cancelTokenSource.token);
		}
		return () => {
			if (cancelTokenSource) {
				cancelTokenSource.cancel();
			}
		};
	}, [action, loadData, shouldFetch]);

	return [data, loading, error, errorStatusCode];
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		progress: {
			margin: theme.spacing(2),
		},
	}),
);

/**
 * Accepts an asynchronous action and renders a loading indicator
 * until the promise is resolved. Data from the resolved action is
 * passed to the provided children render functions
 * @param props
 */
export const Fetcher = ({ action, hidden, children, loadedData, placeHolder, setStateData }: FetcherProps) => {
	const classes = useStyles();
	const stateData = loadedData && loadedData();
	const shouldFetch = stateData == null;
	const [data, loading, error, errorStatusCode] = useFetcher(action, shouldFetch);

	useEffect(() => {
		if (setStateData && data) {
			setStateData!(data!["data"]);
		}
	}, [data, setStateData]);

	if (stateData) {
		return children({ data: stateData });
	}

	if (loading && !hidden)
		return placeHolder ? (
			<span data-testid="fetcher-loading-placeholder">{placeHolder()}</span>
		) : (
			<CircularProgress data-testid="fetcher-loading-indicator" className={classes.progress} />
		);

	if (error)
		return (
			<div data-testid="fetcher-error-msg">
				{<MessageCard message={MSG_BY_STATUS_CODE[errorStatusCode] || error} textAlign="left" />}
			</div>
		);

	if (!data) return null;

	return children(data);
};
